import { axiosInstance } from '@/lib/axios';
import type { LoadStatus } from '../api';

const BASE = '/api/fleet/simulation';

// Mirrors backend/echo/src/modules/fleet/simulation/types.ts
export type SimStep = '5m' | '15m' | '1h' | 'event';
export type SimSource = { loadId: string } | { truckId: string; from: string; to: string };

export interface SimPreview {
  truck: { id: string; plate: string; deviceId: number };
  from: string;
  to: string;
  turas: Array<{ id: string; number: number; route: string; pickupAt: string | null }>;
}

export interface SimTura {
  id: string;
  number: number;
  sourceNumber: number;
  status: LoadStatus;
  phase: string | null;
  flags: string[];
  events: Array<{ at: string; kind: string; source: string; detail: Record<string, unknown> }>;
  claude: { at: string; text: string } | null;
}

export interface SimStatus {
  state: 'ready' | 'stepping' | 'finished';
  ai: boolean;
  sourceTruck: { id: string; plate: string };
  simTruck: { id: string; plate: string };
  from: string;
  to: string;
  clock: string;
  fed: number;
  total: number;
  last: { at: string; speedKmh: number; ignition: boolean | null; vehicleState: string | null } | null;
  turas: SimTura[];
}

export interface SimOverview {
  status: SimStatus | null;
  simTrucks: Array<{ id: string; plate: string }>;
}

/** null when the simulation is off (the server answers 404 to everything) */
export const getSimulation = async (): Promise<SimOverview | null> => {
  try {
    return (await axiosInstance.get<{ data: SimOverview }>(BASE)).data.data;
  } catch (error) {
    // The axios interceptor rejects with a normalized Error carrying the status, not an AxiosError
    if ((error as { status?: number } | null)?.status === 404) return null;
    throw error;
  }
};
export const previewSimulation = async (source: SimSource): Promise<SimPreview> =>
  (await axiosInstance.post<{ data: SimPreview }>(`${BASE}/preview`, source)).data.data;
export const startSimulation = async (input: { source: SimSource; from: string; to: string }): Promise<SimStatus> =>
  (await axiosInstance.post<{ data: SimStatus }>(BASE, input)).data.data;
export const stepSimulation = async (step: SimStep): Promise<SimStatus> =>
  (await axiosInstance.post<{ data: SimStatus }>(`${BASE}/next`, { step })).data.data;
export const restartSimulation = async (): Promise<SimStatus> =>
  (await axiosInstance.post<{ data: SimStatus }>(`${BASE}/restart`)).data.data;
export const stopSimulation = async (): Promise<void> => {
  await axiosInstance.post(`${BASE}/stop`);
};
export const setSimulationAi = async (on: boolean): Promise<SimStatus> =>
  (await axiosInstance.put<{ data: SimStatus }>(`${BASE}/ai`, { on })).data.data;
export const deleteSimTruck = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/trucks/${id}`);
};
